import {
  computed,
  getCurrentScope,
  onMounted,
  onScopeDispose,
  ref,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

/**
 * Legacy parity (`creditConfig.ts`'s `UPGRADE_BOOK_CALL_URL`): always resolvable, so "book a
 * call" is never a dead button waiting on env config — an explicit override (e.g. a
 * `VITE_CREDIT_BOOK_CALL_URL`) just replaces it.
 */
export const DEFAULT_CALENDLY_URL = 'https://calendly.com/bilginyilmaz/orphex_intro_call'

function asHttpUrl(value: string): string | null {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : null
  } catch {
    return null
  }
}

/** Legacy parity (`CreditUpgradeModal.vue`'s/`OnboardingWhoBrand.vue`'s `calendlyUrl`): the same
 * popup-embed query params, so the iframe renders Calendly's popup layout instead of its full
 * page chrome. A malformed override falls back to the trusted default rather than rendering a
 * broken/unsafe `src`. */
export function buildCalendlyEmbedUrl(rawUrl?: string | null): string {
  const configured = rawUrl?.trim()
  const target = (configured && asHttpUrl(configured)) || DEFAULT_CALENDLY_URL
  const url = new URL(target)
  url.searchParams.set('embed_domain', window.location.hostname)
  url.searchParams.set('embed_type', 'PopupWidget')
  url.searchParams.set('hide_gdpr_banner', '1')
  return url.toString()
}

export interface CalendlyBooking {
  isOpen: Ref<boolean>
  loading: Ref<boolean>
  embedUrl: ComputedRef<string>
  open: () => void
  close: () => void
  onFrameLoad: () => void
}

/**
 * Shared "book a call" booking flow — the iframe-popup embed URL plus the
 * `calendly.popup_closed` postMessage listener that auto-dismisses it. Old duplicated this whole
 * pattern verbatim across the credits upgrade modal and onboarding; factoring it out here means
 * every consumer (credits, and any future one — onboarding, get-started, ...) shares one
 * implementation instead of re-deriving it.
 *
 * `url` may resolve to `null`/`undefined` (e.g. no env override configured) — the embed always
 * falls back to `DEFAULT_CALENDLY_URL`, so callers never need to gate rendering on it.
 */
export function useCalendlyBooking(
  url?: MaybeRefOrGetter<string | null | undefined>,
): CalendlyBooking {
  const isOpen = ref(false)
  const loading = ref(false)
  const embedUrl = computed(() => buildCalendlyEmbedUrl(toValue(url)))

  function open(): void {
    loading.value = true
    isOpen.value = true
  }

  function close(): void {
    isOpen.value = false
    loading.value = false
  }

  function onFrameLoad(): void {
    loading.value = false
  }

  function onMessage(event: MessageEvent): void {
    const data = event.data as { event?: unknown } | null | undefined
    if (data?.event === 'calendly.popup_closed') close()
  }

  onMounted(() => window.addEventListener('message', onMessage))
  if (getCurrentScope()) onScopeDispose(() => window.removeEventListener('message', onMessage))

  return { isOpen, loading, embedUrl, open, close, onFrameLoad }
}
